import React, { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import templates from './templates';

const toMins = (t) => {
  if (!t || t === '--:--') return -1;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
};

const toSecs = (t) => {
  if (!t || t === '--:--') return -1;
  const [h, m] = t.split(':').map(Number);
  return h * 3600 + m * 60;
};

const fromMins = (mins) =>
  `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;

const DOW_TO_DAY_KEY = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const PRAYER_LABEL = { shacharit: 'שחרית', mincha: 'מנחה', maariv: 'מעריב' };

export const NextMinyaWidget = ({
  widgetId,
  template = 'Card',
  title = 'מניין הבא',
  showTitle = true,
  location,
  tefilotColor = '#0369a1',
  moadColor = '#b45309',
  shabbatColor = '#7c3aed',
  bgColor = '',
  // Preview mode: bypass Redux entirely
  previewPrayers = null,
  previewNow = null,      // minutes since midnight, e.g. 6*60 = 360
  // Public screen: pass widgets directly to avoid reading from wrong Redux slice
  allWidgetsProp = null,
}) => {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (previewPrayers !== null) return;
    const id = setInterval(() => setNow(new Date()), 1_000);
    return () => clearInterval(id);
  }, [previewPrayers]);

  const allWidgets    = useSelector(state => (previewPrayers !== null || allWidgetsProp !== null) ? [] : (state.dashboard.dashboard?.current?.widgets || []));
  const effectiveWidgets = allWidgetsProp !== null ? allWidgetsProp : allWidgets;
  const engineZmanim  = useSelector(state => state.halachicEngine?.today?.zmanim || null);
  const moadByWidgetId = useSelector(state => state.moad?.byWidgetId || {});
  const shabbatData   = useSelector(state => state.shabbat?.data || null);

  const allPrayerTimes = useMemo(() => {
    if (previewPrayers !== null) return previewPrayers;
    const todayDow     = new Date().getDay();
    const todayDayKey  = DOW_TO_DAY_KEY[todayDow];
    const d            = new Date();
    const todayStr     = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const todayHebrewDay = (() => {
      try { return parseInt(new Intl.DateTimeFormat('he-u-ca-hebrew', { day: 'numeric' }).format(new Date())); }
      catch { return 0; }
    })();
    const isRoshChodesh = todayHebrewDay === 1 || todayHebrewDay === 30;

    const resolveRelative = (baseKey, offset, roundTo, zmanimData) => {
      const map = {
        daySunset:            zmanimData?.sunset,
        dayAlot:              zmanimData?.alotHaShachar,
        dayNetz:              zmanimData?.netzHachama,
        dayChatzot:           zmanimData?.chatzot,
        daySofZmanShma:       zmanimData?.sofZmanShma,
        daySofZmanShmaMGA:    zmanimData?.sofZmanShmaMGA,
        daySofZmanTfilla:     zmanimData?.sofZmanTfilla,
        daySofZmanTfillaMGA:  zmanimData?.sofZmanTfillaMGA,
        dayMinchaGedola:      zmanimData?.minchaGedola,
        dayMinchaKetana:      zmanimData?.minchaKetana,
        dayPlagHamincha:      zmanimData?.plagHamincha,
        dayTzeit85:           zmanimData?.tzeit85deg,
        dayTzeit7083:         zmanimData?.tzeit7083deg,
        dayTzeit42:           zmanimData?.tzeit42min,
        dayTzeit50:           zmanimData?.tzeit50min,
        dayTzeit72:           zmanimData?.tzeit72min,
      };
      const base = map[baseKey];
      if (!base) return '--:--';
      let total = toMins(base) + (parseInt(offset) || 0);
      total = ((total % 1440) + 1440) % 1440;
      if (roundTo > 0) {
        const m = total % 60;
        total = Math.floor(total / 60) * 60 + Math.floor(m / roundTo) * roundTo;
      }
      return fromMins(total);
    };

    const resolveDesc = (desc, zmanimData) => {
      if (!desc) return '--:--';
      if (desc.type === 'fixed') return desc.time || '--:--';
      if (desc.type === 'relative') return resolveRelative(desc.baseTime, desc.offset, desc.roundTo || 0, zmanimData);
      return '--:--';
    };

    const result = [];

    // ── TefilotWidget (יום חול) ──────────────────────────────────────────
    effectiveWidgets.filter(w => w.componentKey === 'TefilotWidget').forEach(tw => {
      const tvFrom = tw.props?.timelineValidFrom;
      const tvTo   = tw.props?.timelineValidTo;
      if (tvFrom && todayStr < tvFrom) return;
      if (tvTo   && todayStr > tvTo)   return;

      const zmanimData = engineZmanim;
      (tw.props?.customTimes || []).forEach(ct => {
        const label = ct.label || PRAYER_LABEL[ct.prayer] || '';
        if (!label) return;
        if (ct.days?.length && !ct.days.includes(todayDayKey)) return;

        const override = (ct.dayOverrides || []).find(ov => {
          const days = ov.days || [];
          return days.includes(todayDow) || (isRoshChodesh && days.includes('roshChodesh'));
        });

        if (ct.type === 'recurring') {
          const startDesc = ct.startType === 'relative'
            ? { type: 'relative', baseTime: ct.startBaseTime, offset: ct.startOffset, roundTo: ct.startRoundTo }
            : { type: 'fixed', time: ct.startTime };
          const endDesc = ct.endType === 'relative'
            ? { type: 'relative', baseTime: ct.endBaseTime, offset: ct.endOffset, roundTo: ct.endRoundTo }
            : { type: 'fixed', time: ct.endTime };
          const start = toMins(resolveDesc(startDesc, zmanimData));
          const end   = toMins(resolveDesc(endDesc, zmanimData));
          const iv    = parseInt(ct.interval) || 30;
          if (start >= 0 && end >= 0) {
            for (let t = start; t <= end; t += iv) {
              result.push({ label, time: fromMins(t), prayer: ct.prayer, color: tefilotColor, source: 'tefilot' });
            }
          }
        } else {
          let time;
          if (override) {
            time = resolveDesc(
              { type: override.type, time: override.time, baseTime: override.baseTime, offset: override.offset, roundTo: override.roundTo },
              zmanimData
            );
          } else {
            time = ct.type === 'fixed'
              ? (ct.time || '--:--')
              : resolveRelative(ct.baseTime, ct.offset, ct.roundTo || 0, zmanimData);
          }
          if (time && time !== '--:--') {
            result.push({ label, time, prayer: ct.prayer, color: tefilotColor, source: 'tefilot' });
          }
        }
      });
    });

    // ── MoadWidget (חג/מועד) — היום בלבד ────────────────────────────────
    effectiveWidgets.filter(w => w.componentKey === 'MoadWidget').forEach(mw => {
      const days        = moadByWidgetId[mw.widgetId]?.data?.days || mw.props?.days || [];
      const customTimes = mw.props?.customTimes || [];
      const moadDay     = days.find(day => day.date === todayStr);
      if (!moadDay) return;

      const moadZmanim = moadDay.zmanim || {};
      const dayIdx     = days.indexOf(moadDay);

      const resolveMoadRelative = (baseKey, off, roundTo) => {
        const map = {
          daySunset:                   moadZmanim.sunset,
          dayAlot:                     moadZmanim.alotHaShachar,
          dayNetz:                     moadZmanim.netzHachama,
          dayChatzot:                  moadZmanim.chatzot,
          dayTzeit85:                  moadZmanim.tzeit85deg,
          dayTzeit7083:                moadZmanim.tzeit7083deg,
          dayTzeit42:                  moadZmanim.tzeit42min,
          dayTzeit50:                  moadZmanim.tzeit50min,
          dayTzeit72:                  moadZmanim.tzeit72min,
          dayMinchaGedola:             moadZmanim.minchaGedola,
          dayMinchaKetana:             moadZmanim.minchaKetana,
          dayPlagHamincha:             moadZmanim.plagHamincha,
          dayEntryTime:                moadDay.entryTime,
          dayExitTime:                 moadDay.exitTime,
          daySofZmanShma:              moadZmanim.sofZmanShma,
          daySofZmanShmaMGA:           moadZmanim.sofZmanShmaMGA,
          daySofZmanTfilla:            moadZmanim.sofZmanTfilla,
          daySofZmanTfillaMGA:         moadZmanim.sofZmanTfillaMGA,
          daySofZmanBiurChametz:       moadZmanim.sofZmanBiurChametz,
          daySofZmanAchilatChametz:    moadZmanim.sofZmanAchilatChametz,
          daySofZmanBiurChametzMGA:    moadZmanim.sofZmanBiurChametzMGA,
          daySofZmanAchilatChametzMGA: moadZmanim.sofZmanAchilatChametzMGA,
        };
        const base = map[baseKey];
        if (!base) return '--:--';
        let total = toMins(base) + (parseInt(off) || 0);
        total = ((total % 1440) + 1440) % 1440;
        if (roundTo > 0) {
          const m = total % 60;
          total = Math.floor(total / 60) * 60 + Math.floor(m / roundTo) * roundTo;
        }
        return fromMins(total);
      };

      customTimes
        .filter(ct => (ct.dayIndex ?? 0) === dayIdx && ct.label)
        .forEach(ct => {
          const time = ct.type === 'relative'
            ? resolveMoadRelative(ct.baseTime, ct.offset, ct.roundTo || 0)
            : (ct.time || '--:--');
          if (!time || time === '--:--') return;
          result.push({ label: ct.label, time, prayer: ct.prayer, color: moadColor, source: 'moad' });
        });
    });

    // ── ShabbatWidget — שישי (ערב שבת) או שבת ──────────────────────────
    if (todayDow === 5 || todayDow === 6) {
      const dayFilter = todayDow === 5 ? 'friday' : 'shabbat';
      effectiveWidgets.filter(w => w.componentKey === 'ShabbatWidget').forEach(sw => {
        const customTimes = sw.props?.customTimes || [];

        if (!shabbatData) {
          // ללא נתוני שבת: מציג רק שעות קבועות
          customTimes
            .filter(ct => (ct.day || 'friday') === dayFilter && ct.type === 'fixed' && ct.time && ct.time !== '--:--')
            .forEach(ct => {
              const label = ct.label || PRAYER_LABEL[ct.prayer] || '';
              if (!label) return;
              result.push({ label, time: ct.time, prayer: ct.prayer, color: shabbatColor, source: 'shabbat' });
            });
          return;
        }

        const dayData = dayFilter === 'shabbat' ? shabbatData.shabbat : shabbatData.friday;
        const baseTimeMap = {
          candleLighting:     shabbatData.meta?.candleLighting,
          havdalah:           shabbatData.shabbat?.havdalah || shabbatData.meta?.havdalah,
          fridaySunset:       shabbatData.friday?.sunset,
          shabbatSunset:      shabbatData.shabbat?.sunset,
          fridayTzeit7083:    shabbatData.friday?.tzeit7083deg,
          fridayTzeit85:      shabbatData.friday?.tzeit85deg,
          fridayTzeit42:      shabbatData.friday?.tzeit42min,
          fridayTzeit50:      shabbatData.friday?.tzeit50min,
          fridayTzeit72:      shabbatData.friday?.tzeit72min,
          shabbatTzeit7083:   shabbatData.shabbat?.tzeit7083deg,
          shabbatTzeit85:     shabbatData.shabbat?.tzeit85deg,
          shabbatTzeit42:     shabbatData.shabbat?.tzeit42min,
          shabbatTzeit50:     shabbatData.shabbat?.tzeit50min,
          shabbatTzeit72:     shabbatData.shabbat?.tzeit72min,
          sofZmanShma:        shabbatData.shabbat?.sofZmanShmaGra,
          sofZmanShmaMga:     shabbatData.shabbat?.sofZmanShmaMga,
          shabbatHavdalah:    shabbatData.shabbat?.havdalah || shabbatData.meta?.havdalah,
          daySunset:          dayData?.sunset,
          dayTzeit7083:       dayData?.tzeit7083deg,
          dayTzeit85:         dayData?.tzeit85deg,
          dayTzeit42:         dayData?.tzeit42min,
          dayTzeit50:         dayData?.tzeit50min,
          dayTzeit72:         dayData?.tzeit72min,
          dayNetz:            dayData?.netzHachama,
          dayAlot:            dayData?.alotHaShachar,
          dayChatzot:         dayData?.chatzot,
          dayPlagHamincha:    dayData?.plagHamincha,
          dayMinchaGedola:    dayData?.minchaGedola,
          dayMinchaKetana:    dayData?.minchaKetana,
          daySofZmanShma:     shabbatData.shabbat?.sofZmanShmaGra,
          dayHavdalah:        shabbatData.shabbat?.havdalah || shabbatData.meta?.havdalah,
        };

        const resolveShabbatRelative = (baseKey, offset, roundTo) => {
          const base = baseTimeMap[baseKey];
          if (!base) return '--:--';
          let total = toMins(base) + (parseInt(offset) || 0);
          total = ((total % 1440) + 1440) % 1440;
          if (roundTo > 0) {
            const m = total % 60;
            total = Math.floor(total / 60) * 60 + Math.floor(m / roundTo) * roundTo;
          }
          return fromMins(total);
        };

        customTimes
          .filter(ct => (ct.day || 'friday') === dayFilter && !String(ct.baseTime || '').startsWith('ct:'))
          .forEach(ct => {
            const label = ct.label || PRAYER_LABEL[ct.prayer] || '';
            if (!label) return;
            const time = ct.type === 'fixed'
              ? (ct.time || '--:--')
              : resolveShabbatRelative(ct.baseTime, ct.offset, ct.roundTo || 0);
            if (!time || time === '--:--') return;
            result.push({ label, time, prayer: ct.prayer, color: shabbatColor, source: 'shabbat' });
          });
      });
    }

    return result
      .filter(p => toMins(p.time) >= 0)
      .sort((a, b) => toMins(a.time) - toMins(b.time));
  }, [effectiveWidgets, engineZmanim, moadByWidgetId, shabbatData, tefilotColor, moadColor, shabbatColor]);

  const nowSecs = previewNow != null
    ? previewNow * 60
    : now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();

  // keep the current prayer visible for 60 seconds after its start time.
  // when all of today's prayers have passed, fall back to showing today's schedule
  // as tomorrow's prayers (marked nextDay: true) so the widget never goes blank.
  const upcomingPrayers = useMemo(() => {
    const todayUpcoming = allPrayerTimes.filter(p => toSecs(p.time) >= nowSecs - 60);
    if (todayUpcoming.length > 0) return todayUpcoming;
    return allPrayerTimes.map(p => ({ ...p, nextDay: true }));
  }, [allPrayerTimes, nowSecs]);

  const nextMinyan = upcomingPrayers[0] ?? null;
  // for next-day prayers the time is "in the past" today, so add 86400s to get
  // the correct seconds-until-prayer countdown across the midnight boundary.
  const secondsLeft = nextMinyan
    ? nextMinyan.nextDay
      ? toSecs(nextMinyan.time) - nowSecs + 86400
      : toSecs(nextMinyan.time) - nowSecs
    : null;

  const hasPrayerSource = previewPrayers !== null || effectiveWidgets.some(w =>
    ['TefilotWidget', 'MoadWidget', 'ShabbatWidget'].includes(w.componentKey)
  );
  const error = !hasPrayerSource ? 'אין רכיבי תפילה בלוח' : null;

  const Template = templates[template] || templates['Card'];

  return (
    <Template
      nextMinyan={nextMinyan}
      secondsLeft={secondsLeft}
      upcomingPrayers={upcomingPrayers}
      allPrayerTimes={allPrayerTimes}
      title={title}
      showTitle={showTitle}
      bgColor={bgColor}
      loading={false}
      error={error}
    />
  );
};
